import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { assessmentService } from '../../api';
import { CheckCircle, XCircle, Trophy, ArrowRight, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';

function AssessmentResult() {
    const { id } = useParams(); // submission id
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        assessmentService.getSubmissionResult(id)
            .then(res => setResult(res.data))
            .catch(() => toast.error('Failed to load result'))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return (
        <DashboardLayout>
            <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
                Loading your result...
            </div>
        </DashboardLayout>
    );

    if (!result) return null;

    const score = result.score ?? 0;
    const total = result.total_marks ?? result.total_questions ?? 100;
    const percentage = result.percentage ?? Math.round((score / total) * 100);
    const passed = result.passed ?? percentage >= 50;
    const assessmentTitle = result.assessment_title || result.assessment?.title || 'Assessment';

    return (
        <DashboardLayout>
            <main className="container" style={{ padding: 'var(--spacing-xl) 0', maxWidth: '680px' }}>

                {/* Result hero card */}
                <div style={{
                    padding: 'var(--spacing-xl)',
                    backgroundColor: 'var(--color-card-bg)',
                    borderRadius: '24px',
                    boxShadow: 'var(--shadow-md)',
                    border: '1px solid var(--color-border)',
                    textAlign: 'center',
                    marginBottom: 'var(--spacing-xl)',
                  color: 'var(--color-text-primary)'
                }}>
                    <div style={{ marginBottom: '1.5rem' }}>
                        {passed
                            ? <Trophy size={64} style={{ color: '#f59e0b' }} />
                            : <XCircle size={64} style={{ color: '#ef4444' }} />
                        }
                    </div>

                    <h2 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                        {passed ? '🎉 Well Done!' : 'Keep Going!'}
                    </h2>
                    <p className="text-muted" style={{ marginBottom: '2rem', fontSize: '1.05rem' }}>
                        {assessmentTitle}
                    </p>

                    {/* Score circle */}
                    <div style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '160px',
                        height: '160px',
                        borderRadius: '50%',
                        border: `8px solid ${passed ? 'var(--color-primary)' : '#ef4444'}`,
                        marginBottom: '2rem',
                        background: passed ? 'var(--color-accent-blue)' : '#fee2e2'
                    }}>
                        <span style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--color-text-primary)' }}>{percentage}%</span>
                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>Score</span>
                    </div>

                    <div className="flex items-center justify-center gap-xl" style={{ marginBottom: '2rem' }}>
                        <div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>{score}</div>
                            <div className="text-muted" style={{ fontSize: '0.8rem' }}>Points Earned</div>
                        </div>
                        <div style={{ width: '1px', height: '40px', backgroundColor: 'var(--color-border)' }} />
                        <div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>{total}</div>
                            <div className="text-muted" style={{ fontSize: '0.8rem' }}>Total Points</div>
                        </div>
                        {result.time_taken_minutes != null && (
                            <>
                                <div style={{ width: '1px', height: '40px', backgroundColor: 'var(--color-border)' }} />
                                <div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>{result.time_taken_minutes}m</div>
                                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>Time Taken</div>
                                </div>
                            </>
                        )}
                    </div>

                    <div style={{
                        display: 'inline-block',
                        padding: '8px 20px',
                        borderRadius: '999px',
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        backgroundColor: passed ? 'var(--color-accent-peach)' : '#fee2e2',
                        color: passed ? 'inherit' : 'var(--color-text-error)',
                        marginBottom: '2rem'
                    }}>
                        {passed ? '✔ PASSED' : '✖ NOT PASSED'}
                    </div>
                </div>

                {/* Per-question breakdown (if available) */}
                {result.answers && result.answers.length > 0 && (
                    <div style={{
                        backgroundColor: 'var(--color-card-bg)',
                        borderRadius: '20px',
                        boxShadow: 'var(--shadow-sm)',
                        border: '1px solid var(--color-border)',
                        marginBottom: 'var(--spacing-xl)',
                        overflow: 'hidden',
                  color: 'var(--color-text-primary)'
                    }}>
                        <div style={{ padding: 'var(--spacing-md) var(--spacing-lg)', borderBottom: '1px solid var(--color-border)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                            Answer Breakdown
                        </div>
                        {result.answers.map((answer, idx) => (
                            <div key={idx} className="flex items-start gap-md" style={{
                                padding: 'var(--spacing-md) var(--spacing-lg)',
                                borderBottom: idx < result.answers.length - 1 ? '1px solid var(--color-border)' : 'none'
                            }}>
                                {answer.is_correct
                                    ? <CheckCircle size={18} style={{ color: 'green', flexShrink: 0, marginTop: '2px' }} />
                                    : <XCircle size={18} style={{ color: '#ef4444', flexShrink: 0, marginTop: '2px' }} />
                                }
                                <div>
                                    <p style={{ margin: 0, fontWeight: 500, color: 'var(--color-text-primary)' }}>Q{idx + 1}: {answer.question_text}</p>
                                    {!answer.is_correct && answer.correct_answer && (
                                        <p className="text-muted" style={{ fontSize: '0.85rem', margin: '4px 0 0' }}>
                                            Correct answer: <strong>{answer.correct_answer}</strong>
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-md">
                    <Link to="/assessments" className="btn" style={{
                        padding: '0.75rem 1.5rem',
                        border: '1px solid var(--color-border)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        textDecoration: 'none',
                        color: 'inherit'
                    }}>
                        <RotateCcw size={16} /> All Assessments
                    </Link>
                    <Link to="/dashboard" className="btn btn-primary flex items-center gap-sm" style={{
                        padding: '0.75rem 1.5rem',
                        color: 'white',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 600
                    }}>
                        Dashboard <ArrowRight size={16} />
                    </Link>
                </div>
            </main>
        </DashboardLayout>
    );
}

export default AssessmentResult;
